import React, { useState, useEffect } from "react";
import { useParams } from "react-router-dom";
import {
  db,
  storage,
  doc,
  getDoc,
  ref,
  getDownloadURL,
} from "../firebaseConfig";
import jsPDF from "jspdf";
import "./DocumentPage.css";
import QRCode from "qrcode";

async function loadFonts(fontFamily) {
  try {
    await document.fonts.load(`12px ${fontFamily}`);
  } catch (error) {
    console.warn(`Font yüklemesi başarısız oldu: ${error.message}`);
  }
}

async function drawTextOnImage(imageUrl, textData, texts, degreeValue = null) {
  return new Promise(async (resolve, reject) => {
    const img = new Image();
    img.crossOrigin = "Anonymous";
    img.src = imageUrl;

    img.onload = async () => {
      const canvas = document.createElement("canvas");
      const ctx = canvas.getContext("2d");
      canvas.width = img.width;
      canvas.height = img.height;
      ctx.drawImage(img, 0, 0);

      try {
        await Promise.all(
          texts.map((text) => loadFonts(text.font_family || "Arial"))
        );

        // Belge tipinin texts dizisinde key: "degree" varsa derece oradaki
        // konum/font ile yazılır; yoksa aşağıdaki sabit konumlu badge kullanılır.
        const hasDegreeText =
          Array.isArray(texts) && texts.some((t) => t.key === "degree");
        if (degreeValue) textData = { ...textData, degree: degreeValue };

        if (texts && Array.isArray(texts)) {
          texts.forEach((text) => {
            const {
              key,
              loc_x,
              loc_y,
              font_size,
              font_family,
              is_bold,
              key_color,
              text_align,
              loc_align,
              max_width,
            } = text;

            if (textData[key]) {
              const color = key_color || "black";
              const fontWeight = is_bold ? "bold" : "normal";
              ctx.font = `${fontWeight} ${font_size}px ${font_family}`;
              ctx.fillStyle = color;

              // max_width verilirse ve yazı bu genişliği aşıyorsa font,
              // yazı sığacak kadar orantılı küçültülür (uzun isimler için).
              const maxWidth = parseFloat(max_width);
              if (maxWidth > 0) {
                const textWidth = ctx.measureText(String(textData[key])).width;
                if (textWidth > maxWidth) {
                  const fittedSize = (font_size * maxWidth) / textWidth;
                  ctx.font = `${fontWeight} ${fittedSize}px ${font_family}`;
                }
              }

              // loc_align ("left" | "center" | "right") verilirse loc_x o hizalamanın
              // noktası olur; ör. "center" → loc_x yazının ortası. text_align ise
              // sadece loc_x yokken kullanılır (mevcut şablonlar bu davranışa göre ayarlı).
              const prevAlign = ctx.textAlign;
              let xPosition;
              if (loc_x !== undefined) {
                xPosition = loc_x;
                if (loc_align) ctx.textAlign = loc_align;
              } else {
                switch (text_align) {
                  case "center":
                    ctx.textAlign = "center";
                    xPosition = canvas.width / 2;
                    break;
                  case "right":
                    ctx.textAlign = "right";
                    xPosition = canvas.width - 10;
                    break;
                  case "left":
                  default:
                    ctx.textAlign = "left";
                    xPosition = 10;
                    break;
                }
              }

              if (key === "degree") {
                // loc_x / loc_y numaranın merkezini gösterir (badge ile aynı)
                ctx.save();
                ctx.textAlign = "center";
                ctx.textBaseline = "middle";
                ctx.fillText(textData[key], xPosition, loc_y);
                ctx.restore();
              } else {
                ctx.fillText(textData[key], xPosition, loc_y);
              }
              if (loc_x !== undefined && loc_align) ctx.textAlign = prevAlign;
            } else {
              console.warn(
                `Metin anahtarı '${key}' metin verisinde bulunamadı.`
              );
            }
          });
        }

        // --- DERECE BADGE --
        if (degreeValue && !hasDegreeText) {
          const badgeX = canvas.width * 0.26; // ör: 21% soldan
          const badgeY = canvas.height * 0.67; // ör: 68% yukarıdan
          const badgeR = canvas.width * 0.090; // badge yarıçapı (genişlikte ölçekleme için)

          ctx.save();
          ctx.font = `bold ${badgeR * 0.9}px Arial`;
          ctx.fillStyle = "#1e2454";
          ctx.textAlign = "center";
          ctx.textBaseline = "middle";
          ctx.shadowColor = "#fff";
          ctx.shadowBlur = 8;
          ctx.fillText(`${degreeValue}`, badgeX, badgeY);
          ctx.restore();
        }

        resolve(canvas);
      } catch (error) {
        reject(new Error("Font yükleme hatası: " + error.message));
      }
    };

    img.onerror = (error) => {
      reject(new Error("Resim yükleme hatası: " + error.message));
    };
  });
}

function isMobileDevice() {
  return /Mobi|Android/i.test(navigator.userAgent);
}

function DocumentPage() {
  const { documentId } = useParams();
  const [pdfUrl, setPdfUrl] = useState(null);
  const [error, setError] = useState(null);
  const [showSuccess, setShowSuccess] = useState(false);
  const [degreeInfo, setDegreeInfo] = useState(null);

  const fetchData = async () => {
    setError(null);
    try {
      const docRef = doc(db, "documents", documentId);
      const docSnap = await getDoc(docRef);

      if (!docSnap.exists()) {
        throw new Error("Belge bulunamadı.");
      }

      const data = docSnap.data();

      const docType = documentId.endsWith("sebf")
        ? "yarismalar-sebf"
        : data.document_type;

      const docTypeRef = doc(db, "document_types", docType);
      const docTypeSnap = await getDoc(docTypeRef);

      if (!docTypeSnap.exists()) {
        throw new Error("Belge tipi bulunamadı.");
      }

      const docTypeData = docTypeSnap.data();
      const isGameDocument = Boolean(
        docTypeData?.is_game ||
          (docTypeData?.title &&
            docTypeData.title.toLowerCase().includes("oyun")) ||
          docType?.toLowerCase()?.includes("oyun") ||
          docTypeData?.category === "game"
      );

      // Sadece document_type içinde derece geçiyorsa derece bilgisini yaz
      const docTypeHasDegree =
        data.document_type &&
        data.document_type.toLowerCase().includes("derece");
      setDegreeInfo(docTypeHasDegree ? data.degree : null);

      const imageRef = ref(
        storage,
        `background_images/${docTypeData.background_image_path}`
      );
      const imageUrl = await getDownloadURL(imageRef);

      let textData;
      if (docTypeData.background_image_path.includes("okul")) {
        // Eğer background_image_path içinde "okul" kelimesi geçiyorsa
        textData = {
          school_name: `${data.school_name}`,
          city: `(${data.city}/${data.district})`,
        };
      } else if (
        documentId.endsWith("sebf") ||
        data.document_type.endsWith("sebf")
      ) {
        // SEBF belgeleri için
        textData = {
          organizer_institution: data.organizer_institution,
          document_date: data.document_date,
          document_number: data.document_number,
          event_start_date: data.event_start_date,
          event_end_date: data.event_end_date,
          event_city: data.event_city,
          event_district: data.event_district,
          event_location: data.event_location,
          document_number_two: data.document_number_two,
          start_date_two: data.start_date_two,
          event_name: data.event_name,
          representation_level_province: data.representation_level_province,
          representation_level_area: data.representation_level_area,
          representation_level_national: data.representation_level_national,
          representation_level_international:
            data.representation_level_international,
          event_level_participation: data.event_level_participation,
          event_level_performance: data.event_level_performance,
          user_name: data.user_name,
          school_name: `${data.school_name} (${data.city}/${data.district})`,
        };
      } else {
        // Diğer belgeler için
        textData = {
          user_name: data.user_name,
          school_name: `${data.school_name} (${data.city}/${data.district})`,
          city: `(${data.city}/${data.district})`,
        };
      }

      const canvas = await drawTextOnImage(
        imageUrl,
        textData,
        docTypeData.texts,
        docTypeHasDegree ? data.degree : null
      );
      await handleSaveAndOpenPdf(
        canvas,
        documentId,
        docTypeData.portrait,
        isGameDocument
      );
    } catch (error) {
      console.error("Veri alınırken sorun yaşandı:", error);
      setError(`Hata: ${error.message}`);
    }
  };

  const handleSaveAndOpenPdf = async (
    canvas,
    documentId,
    isPortrait,
    isGameDocument
  ) => {
    if (canvas) {
      const pdfWidth = isPortrait ? 595 : 842;
      const pdfHeight = isPortrait ? 842 : 595;

      const pdf = new jsPDF({
        orientation: isPortrait ? "portrait" : "landscape",
        unit: "px",
        format: [pdfWidth, pdfHeight],
      });

      const imgData = canvas.toDataURL("image/jpeg", 1.0);
      pdf.addImage(imgData, "JPEG", 0, 0, pdfWidth, pdfHeight);

      if (
        !isGameDocument &&
        documentId?.length !== 11 &&
        !documentId.endsWith("ibb")
      ) {
        const baseUrl = window.location.origin;
        const qrCodeUrl = `${baseUrl}/belgelerim/${documentId}`;

        try {
          const qrCodeDataUrl = await QRCode.toDataURL(qrCodeUrl);
          pdf.addImage(qrCodeDataUrl, "PNG", pdfWidth - 100, 30, 70, 70);
        } catch (error) {
          console.error("QR kod üretimi başarısız oldu:", error);
        }
      }

      // --- DERECEYİ SOL ALTA YAZ ---
      // if (degreeInfo) {
      //   pdf.setFontSize(18);
      //   pdf.setFont(undefined, "bold");
      //   pdf.text(`Derece: ${degreeInfo}`, 20, pdfHeight - 20, {
      //     align: "left",
      //   }); // 20 px soldan, alta yakın
      // }
      // --- SONU ---

      const pdfOutput = pdf.output("blob");

      if (isMobileDevice()) {
        const url = URL.createObjectURL(pdfOutput);
        const a = document.createElement("a");
        a.href = url;
        a.download = `document_${documentId}.pdf`;
        document.body.appendChild(a);
        a.click();
        document.body.removeChild(a);
        URL.revokeObjectURL(url);
        setPdfUrl(null);
        setShowSuccess(true);
      } else {
        const pdfUrl = URL.createObjectURL(pdfOutput);
        setPdfUrl(pdfUrl);
      }
    } else {
      setError("Belge henüz oluşturulamadı.");
    }
  };
  useEffect(() => {
    fetchData();
  }, [documentId]);

  return (
    <div>
      {error && <div className="error-message">{error}</div>}
      {!isMobileDevice() && pdfUrl && (
        <iframe src={pdfUrl} className="pdf-viewer" title="PDF Viewer" />
      )}
      {isMobileDevice() && showSuccess && (
        <div className="success-message">Belgeniz indirilmiştir.</div>
      )}
    </div>
  );
}

export default DocumentPage;
